import { createRouter, createWebHistory } from 'vue-router'

import { Roles } from '@/enums/Roles.ts'
import { hasRoles, isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition
        else if (to.hash) return { selector: to.hash }
        else if (from.path === to.path) return {}

        return {
            top: 1,
            behavior: 'smooth'
        }
    },
    routes: [
        {
            path: '/',
            name: 'dashboard',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/users',
            name: 'users',
            component: () => import('@/views/User/Users.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/channel-categories',
            name: 'channel-categories',
            component: () => import('@/views/channelCategory/ChannelCategories.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/channel-packages',
            name: 'channel-packages',
            component: () => import('@/views/channelPackage/ChannelPackages.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/add-channel-package',
            name: 'add-channel-package',
            component: () => import('@/views/channelPackage/AddChannelPackage.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/edit-channel-package/:id',
            name: 'edit-channel-package',
            component: () => import('@/views/channelPackage/EditChannelPackage.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/channels',
            name: 'channels',
            component: () => import('@/views/channel/Channels.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/channel/:id',
            name: 'channel',
            component: () => import('@/views/channel/Channel.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/add-channel',
            name: 'add-channel',
            component: () => import('@/views/channel/AddChannel.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/sort-channels',
            name: 'sort-channels',
            component: () => import('@/views/channel/SortChannels.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/live-media-video-profiles',
            name: 'live-media-video-profiles',
            component: () => import('@/views/liveMediaProfile/LiveMediaVideoProfiles.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/live-media-audio-profiles',
            name: 'live-media-audio-profiles',
            component: () => import('@/views/liveMediaProfile/LiveMediaAudioProfiles.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/live-media-sources',
            name: 'live-media-sources',
            component: () => import('@/views/liveMediaSource/LiveMediaSources.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/epg-providers',
            name: 'epg-providers',
            component: () => import('@/views/EPG/EPGProviders.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/epg-provider/:id',
            name: 'epg-provider',
            component: () => import('@/views/EPG/EPGProvider.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/profile',
            name: 'profile',
            component: () => import('@/views/profile/Profile.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/distribution-zones',
            name: 'distribution-zones',
            component: () => import('@/views/distributionZone/DistributionZones.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/clients',
            name: 'clients',
            component: () => import('@/views/client/Clients.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/client/:id',
            name: 'client',
            component: () => import('@/views/client/Client.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/session/:id',
            name: 'session',
            component: () => import('@/views/session/Session.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/workers',
            name: 'workers',
            component: () => import('@/views/worker/Workers.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/workers/:id',
            name: 'worker',
            component: () => import('@/views/worker/Worker.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/edit-worker/:id',
            name: 'edit-worker',
            component: () => import('@/views/worker/EditWorker.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/tls-configurations',
            name: 'tls-configurations',
            component: () => import('@/views/TLSConfiguration/TLSConfigurations.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/tls-configuration/:id',
            name: 'tls-configuration',
            component: () => import('@/views/TLSConfiguration/TLSConfiguration.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/add-tls-configuration',
            name: 'add-tls-configuration',
            component: () => import('@/views/TLSConfiguration/AddTLSConfiguration.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/api-keys',
            name: 'api-keys',
            component: () => import('@/views/apiKey/APIKeys.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/global-drm',
            name: 'global-drm',
            component: () => import('@/views/globalDRM/GlobalDrm.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Roles.GlobalDRMConfigurationRead]
            }
        },
        {
            path: '/distribution-zone/:id',
            name: 'distribution-zone',
            component: () => import('@/views/distributionZone/DistributionZone.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/settings',
            name: 'settings',
            component: () => import('@/views/Settings.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/Login.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false
            }
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/'
        }
    ]
})

router.beforeEach((to, from, next) => {
    const loggedIn: boolean = isAuthenticated()
    const appStore = useAppStore()

    if (loggedIn && to.name === 'login') {
        next({ name: 'dashboard' })
        return
    }

    if (to.meta.requiresAuth && !isAuthenticated()) {
        appStore.setLogout()
        next({ name: 'login' })
    }

    if (to.meta.requiredRoles && isAuthenticated()) {
        if (!hasRoles(...to.meta.requiredRoles)) {
            next({ name: 'dashboard' })
        }
    }

    next()
})

export default router
